import { isEqual } from "lodash-es";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";

import Box from "ds/components/Box";
import DrawerBody from "ds/components/Drawer/Body";
import DrawerHeader from "ds/components/Drawer/Header";
import DrawerCloseIcon from "ds/components/DrawerNew/CloseIcon";
import DrawerHeaderTitle from "ds/components/DrawerNew/HeaderTitle";
import DrawerSimple from "ds/components/DrawerNew/Simple";
import { createDrawer, createDrawerTrigger } from "ds/components/DrawerNew/utils";
import Tab from "ds/components/Tab";
import { AnalyticsPage } from "hooks/useAnalytics";
import usePrevious from "hooks/usePrevious";
import { useToggle } from "hooks/useToggle";

import { TASK_SEARCH_KEY } from "../../constants";
import { ConfigNode } from "../../types";
import ConfigManagementTreeGridDrawerDetails from "./Details";
import ConfigManagementTreeGridDrawerDiff from "./Diff";
import ConfigManagementTreeGridDrawerDiffFullScreen from "./DiffFullScreen";
import ConfigManagementTreeGridDrawerLogs from "./Logs";
import ConfigManagementTreeGridDrawerLogsFullScreen from "./LogsFullScreen";
import { DrawerTabs } from "./types";
import { DiffEntry, formatDiffSide, getDiffLanguage, parseDiff } from "./utils";

type ConfigManagementTreeGridDrawerProps = {
  task?: ConfigNode;
  analyticsPage?: AnalyticsPage;
};

const ConfigManagementTreeGridDrawer = createDrawer(
  ({ task, analyticsPage }: ConfigManagementTreeGridDrawerProps) => {
    const [logsFullScreen, toggleLogsFullScreen] = useToggle(false);
    const [diffFullScreenEntry, setDiffFullScreenEntry] = useState<DiffEntry>();
    const [tab, setTab] = useState(DrawerTabs.Details);

    const handleTabChange = useCallback((tab: DrawerTabs) => {
      setTab(tab);
    }, []);

    const [diffFileName, setDiffFileName] = useState<string | undefined>();

    const parsedDiff = useMemo(() => parseDiff(task?.diff), [task?.diff]);

    const options = useMemo(
      () => Object.keys(parsedDiff).map((key) => ({ label: key, value: key })),
      [parsedDiff]
    );

    const previousOptions = usePrevious(options);

    useEffect(() => {
      if (!isEqual(previousOptions, options) && diffFileName !== undefined) {
        setDiffFileName(undefined);
      }
    }, [options, previousOptions, diffFileName]);

    const currentDiffFileName = diffFileName || options[0]?.value;
    const currentDiff = parsedDiff[currentDiffFileName];
    const original = currentDiff ? formatDiffSide(currentDiff.before) : "";
    const modified = currentDiff ? formatDiffSide(currentDiff.after) : "";
    const language = currentDiff ? getDiffLanguage(currentDiff) : "plaintext";

    const diffFullScreen = !!currentDiff && diffFullScreenEntry === currentDiff;
    const toggleDiffFullScreen = useCallback(
      (open?: boolean) => setDiffFullScreenEntry(open ? currentDiff : undefined),
      [currentDiff]
    );

    const navigate = useNavigate();
    const location = useLocation();
    const newUrlParams = useMemo(() => {
      const params = new URLSearchParams(location.search);
      params.delete(TASK_SEARCH_KEY);
      return params;
    }, [location.search]);

    const handleCloseDrawer = () => {
      navigate(`${location.pathname}${newUrlParams ? `?${newUrlParams}` : ""}`);
      toggleLogsFullScreen(false);
      toggleDiffFullScreen(false);
      setTab(DrawerTabs.Details);
    };

    useEffect(() => {
      if (!task && logsFullScreen) {
        toggleLogsFullScreen(false);
        setTab(DrawerTabs.Details);
      }
    }, [task, logsFullScreen, toggleLogsFullScreen]);

    const logsFileName = `${task?.name}.logs.txt`;
    const isTaskAvailable = !!task;

    const shouldCloseOnInteractOutside = useCallback((el: Element) => {
      if (el.hasAttribute("data-tree-chart-element-interactive")) {
        return false;
      }

      const hrefElem = el.getAttribute("href") || el.parentElement?.getAttribute("href");

      if (hrefElem?.startsWith("/resources/configuration-management?task=")) {
        return false;
      }

      const modal = document.querySelector("[data-fullscreen-modal-wrapper]");

      if (modal && modal.contains(el)) {
        return false;
      }

      return true;
    }, []);

    return (
      <>
        <DrawerSimple
          onClose={handleCloseDrawer}
          shouldCloseOnInteractOutside={shouldCloseOnInteractOutside}
        >
          <DrawerHeader justify="between">
            <DrawerHeaderTitle title="Task details" />
            <DrawerCloseIcon />
          </DrawerHeader>
          <DrawerBody direction="column" fullWidth>
            <Box gap="medium">
              <Tab
                label="Details"
                id={DrawerTabs.Details}
                onClick={handleTabChange}
                isActive={tab === DrawerTabs.Details}
              />
              <Tab
                label="Logs"
                id={DrawerTabs.Logs}
                onClick={handleTabChange}
                isActive={tab === DrawerTabs.Logs}
              />
              <Tab
                label="Diff"
                id={DrawerTabs.Diff}
                onClick={handleTabChange}
                isActive={tab === DrawerTabs.Diff}
              />
            </Box>
            {isTaskAvailable && (
              <>
                {tab === DrawerTabs.Details && (
                  <ConfigManagementTreeGridDrawerDetails task={task} />
                )}
                {tab === DrawerTabs.Logs && (
                  <ConfigManagementTreeGridDrawerLogs
                    task={task}
                    toggleFullScreen={toggleLogsFullScreen}
                    fileName={logsFileName}
                  />
                )}
                {tab === DrawerTabs.Diff && (
                  <ConfigManagementTreeGridDrawerDiff
                    options={options}
                    original={original}
                    modified={modified}
                    language={language}
                    setFileName={setDiffFileName}
                    fileName={currentDiffFileName}
                    toggleFullScreen={toggleDiffFullScreen}
                  />
                )}
              </>
            )}
          </DrawerBody>
        </DrawerSimple>
        {tab === DrawerTabs.Logs && isTaskAvailable && (
          <ConfigManagementTreeGridDrawerLogsFullScreen
            task={task}
            toggleFullScreen={toggleLogsFullScreen}
            fullScreen={logsFullScreen}
            fileName={logsFileName}
            analyticsPage={analyticsPage}
          />
        )}
        {tab === DrawerTabs.Diff && currentDiff && (
          <ConfigManagementTreeGridDrawerDiffFullScreen
            original={original}
            modified={modified}
            language={language}
            toggleFullScreen={toggleDiffFullScreen}
            fullScreen={diffFullScreen}
          />
        )}
      </>
    );
  }
);

export const showConfigManagementTreeGridDrawer = createDrawerTrigger(
  ConfigManagementTreeGridDrawer
);
